import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Modal from "react-modal";
import ZaloPaySDK from "../../utils/zalopay";
import Utils from "../../utils/utils";
import "./FireExplosionInsuranceScreen.scss";
import { useDispatch, useSelector } from "react-redux";
import { getInsuranceFireExplosionProduct } from "../../stores/insurance/insurance.action";
import { clearErrMess, clearPaymentInfo, createOrderCallback, payOrder } from "../../stores/payment/payment.action";
import { PRODUCT_TYPE, TYPE_INSURANCE } from "../../utils/const";
import { updateCustomerInfo } from "../../stores/customer/customer.action";
import {
  BENEFIT_SHEET_TABS,
  MIN_DAYS_FROM_NOW,
  SCREEN_TITLE,
} from "./constants";
import {
  getFireExplosionVariantKey,
  normalizeFireExplosionPackages,
} from "./packageUtils";
import BenefitSheetModal from "./components/BenefitSheetModal";
import BuyerInfoSection from "./components/BuyerInfoSection";
import FireExplosionPackagesPanel from "./components/FireExplosionPackagesPanel";
import { AppApiInstance } from "api/app-api";
import { formatVnd } from "./packageUtils";

const FireExplosionInsuranceScreen = () => {
  const {
    gettingInsuranceFireExplosionProduct,
    insuranceFireExplosionProduct,
    getInsuranceFireExplosionProductErrMsg,
  } = useSelector((state) => state.insurance);

  const {
    createPayOrder,
    isPayment,
    paymentInfo,
    createPayOrderErrMsg,
    creatingOrder,
    createOrderErrMsg,
  } = useSelector((state) => state.payment);
  const { customerInfo } = useSelector((state) => state.customer);
  const dispatch = useDispatch();

  const [selectedId, setSelectedId] = useState("");
  /** Khóa biến thể theo công ty (productId hoặc fallback) trong gói đang chọn */
  const [selectedVariantKey, setSelectedVariantKey] = useState("");
  const [sameAsInsuredAddress, setSameAsInsuredAddress] = useState(true);
  const packagesSectionRef = useRef(null);
  const isPayingRef = useRef(false);
  const processedPaymentRef = useRef(null);

  const scrollToPackages = () => {
    if (packagesSectionRef.current) {
      packagesSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleToggleSameAddress = (checked) => {
    setSameAsInsuredAddress(checked);
    if (checked) {
      setForm((prev) => ({
        ...prev,
        buyerProvince: prev.insuredProvince,
        buyerWard: prev.insuredWard,
        address: prev.insuredAddress,
      }));
      if (insuredWards && insuredWards.length > 0) {
        setBuyerWards(insuredWards);
      }
    } else {
      setForm((prev) => ({
        ...prev,
        buyerProvince: null,
        buyerWard: null,
        address: "",
      }));
      setBuyerWards([]);
    }
  };
  const [form, setForm] = useState({
    buyerType: "non_commercial",
    insuredAddress: "",
    sellerName: "",
    buyerName: customerInfo && customerInfo.fullName ? customerInfo.fullName : "",
    buyerPhone: customerInfo && customerInfo.phone ? customerInfo.phone : "",
    email: customerInfo && customerInfo.email ? customerInfo.email : "",
    address: customerInfo && customerInfo.address ? customerInfo.address : "",
    insuredProvince: null,
    insuredWard: null,
    buyerProvince: null,
    buyerWard: null,
  });
  const [fieldTouched, setFieldTouched] = useState({
    insuredAddress: false,
    buyerName: false,
    buyerPhone: false,
    email: false,
    address: false,
    insuredProvince: false,
    insuredWard: false,
    buyerProvince: false,
    buyerWard: false,
  });

  const [provinces, setProvinces] = useState([]);
  const [insuredWards, setInsuredWards] = useState([]);
  const [buyerWards, setBuyerWards] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingInsuredWards, setLoadingInsuredWards] = useState(false);
  const [loadingBuyerWards, setLoadingBuyerWards] = useState(false);

  const minDateEffect = useMemo(
    () => moment().add(MIN_DAYS_FROM_NOW, "days").startOf("date").toDate(),
    []
  );
  const maxDateEffect = useMemo(
    () =>
      moment()
        .startOf("date")
        .add(365 + MIN_DAYS_FROM_NOW, "days")
        .toDate(),
    []
  );

  const [dateEffect, setDateEffect] = useState(() => new Date());
  const [benefitSheetOpen, setBenefitSheetOpen] = useState(false);
  const [benefitSheetTab, setBenefitSheetTab] = useState(BENEFIT_SHEET_TABS.detail);

  const closeBenefitSheet = () => setBenefitSheetOpen(false);

  const openBenefitSheet = (tab) => {
    setBenefitSheetTab(tab);
    setBenefitSheetOpen(true);
  };

  useEffect(() => {
    const root = document.getElementById("root");
    if (root) Modal.setAppElement("#root");
  }, []);

  useEffect(() => {
    if (benefitSheetOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [benefitSheetOpen]);

  useEffect(() => {
    ZaloPaySDK.setTitleV2(SCREEN_TITLE);
  }, []);

  useEffect(() => {
    customerInfo.fullName = form.buyerName;
    customerInfo.phone = form.buyerPhone;
    customerInfo.email = form.email;
    customerInfo.address = sameAsInsuredAddress ? form.insuredAddress : form.address;
    customerInfo.insuredProvince = form.insuredProvince;
    customerInfo.insuredWard = form.insuredWard;
    customerInfo.buyerProvince = sameAsInsuredAddress ? form.insuredProvince : form.buyerProvince;
    customerInfo.buyerWard = sameAsInsuredAddress ? form.insuredWard : form.buyerWard;
    dispatch(updateCustomerInfo(customerInfo));
  }, [form, sameAsInsuredAddress, dispatch]);

  useEffect(() => {
    dispatch(getInsuranceFireExplosionProduct());
  }, [dispatch]);

  useEffect(() => {
    setLoadingProvinces(true);
    AppApiInstance.getPviPropertyAllRisksCategories()
      .then((res) => {
        const items = res?.data?.getPviPropertyAllRisksCategories || [];
        const mapped = items.map((item) => ({
          code: item.Value,
          name: item.Text,
          nameWithType: item.Text,
          disabled: item.Disabled,
          group: item.Group,
          selected: item.Selected,
        }));
        setProvinces(mapped);
      })
      .catch((err) => {
        console.error("Failed to fetch PVI provinces:", err);
      })
      .finally(() => {
        setLoadingProvinces(false);
      });
  }, []);

  const fetchWards = (provinceCode, setter, setLoading) => {
    setLoading(true);
    AppApiInstance.getPviPropertyAllRisksCategories({ parentValue: provinceCode })
      .then((res) => {
        const items = res?.data?.getPviPropertyAllRisksCategories || [];
        const mapped = items.map((item) => ({
          code: item.Value,
          name: item.Text,
          nameWithType: item.Text,
          disabled: item.Disabled,
          group: item.Group,
          selected: item.Selected,
        }));
        setter(mapped);
      })
      .catch((err) => {
        console.error("Failed to fetch PVI wards:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInsuredProvinceChange = (provinceValue) => {
    updateField("insuredProvince", provinceValue);
    updateField("insuredWard", null);
    setInsuredWards([]);
    if (sameAsInsuredAddress) {
      updateField("buyerProvince", provinceValue);
      updateField("buyerWard", null);
      setBuyerWards([]);
    }
    if (provinceValue?.code) {
      fetchWards(provinceValue.code, setInsuredWards, setLoadingInsuredWards);
      if (sameAsInsuredAddress) {
        fetchWards(provinceValue.code, setBuyerWards, setLoadingBuyerWards);
      }
    }
  };

  const handleBuyerProvinceChange = (provinceValue) => {
    updateField("buyerProvince", provinceValue);
    updateField("buyerWard", null);
    setBuyerWards([]);
    if (provinceValue?.code) {
      fetchWards(provinceValue.code, setBuyerWards, setLoadingBuyerWards);
    }
  };

  useEffect(() => {
    if (gettingInsuranceFireExplosionProduct) {
      ZaloPaySDK.showLoading();
    } else {
      ZaloPaySDK.hideLoading();
    }
  }, [gettingInsuranceFireExplosionProduct]);

  useEffect(() => {
    if (getInsuranceFireExplosionProductErrMsg) {
      ZaloPaySDK.showDialog({
        message: getInsuranceFireExplosionProductErrMsg,
        buttons: [{ text: "Đóng" }],
      });
    }
  }, [getInsuranceFireExplosionProductErrMsg]);

  const cb = (data) => {
    console.log("function callback", data);
    isPayingRef.current = false;
    dispatch(clearPaymentInfo());
  };

  useEffect(() => {
    const currentPaymentKey = paymentInfo?.zptranstoken || paymentInfo?.order_url;

    if (paymentInfo && isPayment && currentPaymentKey) {
      if (processedPaymentRef.current !== currentPaymentKey) {
        processedPaymentRef.current = currentPaymentKey;
        isPayingRef.current = true;
        ZaloPaySDK.payOrder(paymentInfo, cb);
      }
    } else if (!isPayment) {
      isPayingRef.current = false;
      processedPaymentRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo, isPayment]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        isPayingRef.current = false;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearPaymentInfo());
    };
  }, [dispatch]);

  useEffect(() => {
    if (createPayOrder) {
      ZaloPaySDK.showLoading();
    } else {
      ZaloPaySDK.hideLoading();
    }
  }, [createPayOrder]);

  useEffect(() => {
    if (createPayOrderErrMsg) {
      ZaloPaySDK.showDialog({
        message: createPayOrderErrMsg,
        buttons: [{ text: "Đóng" }],
      });
    }
  }, [createPayOrderErrMsg]);

  useEffect(() => {
    if (creatingOrder) {
      ZaloPaySDK.showLoading();
    } else {
      ZaloPaySDK.hideLoading();
    }
  }, [creatingOrder]);

  useEffect(() => {
    console.log("createOrderErrMsg", createOrderErrMsg);
    if (createOrderErrMsg) {
      ZaloPaySDK.showDialog({
        message: createOrderErrMsg,
        buttons: [{ text: "Đóng" }],
      });
      dispatch(clearErrMess());
    }
  }, [createOrderErrMsg, dispatch]);

  const packagesNormalized = useMemo(
    () => normalizeFireExplosionPackages(insuranceFireExplosionProduct),
    [insuranceFireExplosionProduct]
  );

  useEffect(() => {
    if (!packagesNormalized.length) {
      if (selectedId) setSelectedId("");
      if (selectedVariantKey) setSelectedVariantKey("");
      return;
    }
    const tier =
      packagesNormalized.find((pkg) => pkg.id === selectedId) ||
      packagesNormalized[0];
    if (tier.id !== selectedId) {
      setSelectedId(tier.id);
    }
    const keys = tier.variants.map((v, i) => getFireExplosionVariantKey(v, i));
    if (!keys.includes(selectedVariantKey)) {
      setSelectedVariantKey(getFireExplosionVariantKey(tier.variants[0], 0));
    }
  }, [packagesNormalized, selectedId, selectedVariantKey]);

  const displayPackages = useMemo(() => {
    return packagesNormalized.map((tier) => {
      const premiums = tier.variants.map((v) => v.premium);
      const minP = Math.min(...premiums);
      const maxP = Math.max(...premiums);
      const isActive = tier.id === selectedId;
      let premium = minP;
      let pricePrefix = "";
      if (isActive) {
        const idx = tier.variants.findIndex(
          (v, i) => getFireExplosionVariantKey(v, i) === selectedVariantKey
        );
        const v = idx >= 0 ? tier.variants[idx] : tier.variants[0];
        premium = v.premium;
      } else if (tier.variants.length > 1 && maxP !== minP) {
        pricePrefix = "Từ ";
        premium = minP;
      }
      return { ...tier, premium, pricePrefix };
    });
  }, [packagesNormalized, selectedId, selectedVariantKey]);

  const selectedTier = packagesNormalized.find((p) => p.id === selectedId);

  const selectedPackage = useMemo(() => {
    if (!selectedTier?.variants?.length) return null;
    const idx = selectedTier.variants.findIndex(
      (v, i) => getFireExplosionVariantKey(v, i) === selectedVariantKey
    );
    return idx >= 0 ? selectedTier.variants[idx] : selectedTier.variants[0];
  }, [selectedTier, selectedVariantKey]);

  const insuredAddressError = useMemo(() => {
    if (!fieldTouched.insuredAddress) return "";
    if (!form.insuredAddress?.trim()) return "Vui lòng nhập địa chỉ được bảo hiểm";
    return "";
  }, [form.insuredAddress, fieldTouched.insuredAddress]);

  const insuredProvinceError = useMemo(() => {
    if (!fieldTouched.insuredProvince) return "";
    if (!form.insuredProvince?.code) return "Vui lòng chọn Tỉnh thành (địa chỉ bảo hiểm)";
    return "";
  }, [form.insuredProvince, fieldTouched.insuredProvince]);

  const insuredWardError = useMemo(() => {
    if (!fieldTouched.insuredWard) return "";
    if (!form.insuredWard?.code) return "Vui lòng chọn Phường/Xã (địa chỉ bảo hiểm)";
    return "";
  }, [form.insuredWard, fieldTouched.insuredWard]);

  const buyerProvinceError = useMemo(() => {
    if (sameAsInsuredAddress) return "";
    if (!fieldTouched.buyerProvince) return "";
    if (!form.buyerProvince?.code) return "Vui lòng chọn Tỉnh thành";
    return "";
  }, [form.buyerProvince, fieldTouched.buyerProvince, sameAsInsuredAddress]);

  const buyerWardError = useMemo(() => {
    if (sameAsInsuredAddress) return "";
    if (!fieldTouched.buyerWard) return "";
    if (!form.buyerWard?.code) return "Vui lòng chọn Phường/Xã";
    return "";
  }, [form.buyerWard, fieldTouched.buyerWard, sameAsInsuredAddress]);

  const buyerNameError = useMemo(() => {
    if (!fieldTouched.buyerName) return "";
    if (!form.buyerName?.trim()) return "Vui lòng nhập tên bên mua bảo hiểm";
    return "";
  }, [form.buyerName, fieldTouched.buyerName]);

  const phoneError = useMemo(() => {
    if (!fieldTouched.buyerPhone) return "";
    if (!form.buyerPhone?.trim()) return "Vui lòng nhập số điện thoại";
    if (!Utils.validateVietnamPhone(form.buyerPhone)) {
      return "Số điện thoại không hợp lệ (VD: 0912345678 hoặc 84912345678)";
    }
    return "";
  }, [form.buyerPhone, fieldTouched.buyerPhone]);

  const emailError = useMemo(() => {
    if (!fieldTouched.email) return "";
    if (!form.email?.trim()) return "Vui lòng nhập email";
    if (!Utils.validateEmail(form.email.trim())) return "Email không hợp lệ";
    return "";
  }, [form.email, fieldTouched.email]);

  const addressError = useMemo(() => {
    if (sameAsInsuredAddress) return "";
    if (!fieldTouched.address) return "";
    if (!form.address?.trim()) return "Vui lòng nhập địa chỉ";
    return "";
  }, [form.address, fieldTouched.address, sameAsInsuredAddress]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleBuy = () => {
    if (creatingOrder || createPayOrder || isPayingRef.current) {
      return;
    }
    const touchState = {
      insuredAddress: true,
      buyerName: true,
      buyerPhone: true,
      email: true,
      insuredProvince: true,
      insuredWard: true,
    };
    if (!sameAsInsuredAddress) {
      touchState.buyerProvince = true;
      touchState.buyerWard = true;
      touchState.address = true;
    }
    setFieldTouched((prev) => ({ ...prev, ...touchState }));

    ZaloPaySDK.showLoading();
    if (!selectedPackage?.productId) {
      ZaloPaySDK.hideLoading();
      ZaloPaySDK.showDialog({
        message: "Vui lòng chọn gói và công ty bảo hiểm",
        buttons: [{ text: "Đóng" }],
      });
      return;
    }
    if (!dateEffect) {
      ZaloPaySDK.hideLoading();
      ZaloPaySDK.showDialog({
        message: "Vui lòng chọn ngày hiệu lực",
        buttons: [{ text: "Đóng" }],
      });
      return;
    }

    const hasInvalidBuyerAddress =
      !sameAsInsuredAddress &&
      (!form.buyerProvince?.code ||
        !form.buyerWard?.code ||
        !form.address?.trim());

    const hasInvalidRequired =
      !form.insuredAddress?.trim() ||
      !form.insuredProvince?.code ||
      !form.insuredWard?.code ||
      !form.buyerName?.trim() ||
      !form.email?.trim() ||
      !Utils.validateEmail(form.email?.trim() || "") ||
      !Utils.validateVietnamPhone(form.buyerPhone) ||
      hasInvalidBuyerAddress;

    if (hasInvalidRequired) {
      ZaloPaySDK.hideLoading();
      ZaloPaySDK.showDialog({
        message: "Vui lòng kiểm tra đầy đủ thông tin bắt buộc trong biểu mẫu.",
        buttons: [{ text: "Đóng" }],
      });
      return;
    }

    const finalBuyerProvince = sameAsInsuredAddress
      ? form.insuredProvince
      : form.buyerProvince;
    const finalBuyerWard = sameAsInsuredAddress
      ? form.insuredWard
      : form.buyerWard;
    const finalBuyerAddress = sameAsInsuredAddress
      ? form.insuredAddress
      : form.address;

    const now = moment();
    const isFutureDate = moment(dateEffect).startOf("date").isAfter(now.clone().startOf("date"));

    let effectiveMoment;
    let expiryMoment;

    if (isFutureDate) {
      // Ngày hiệu lực lớn hơn ngày hiện tại: giờ bắt đầu là 00:00:00, ngày kết thúc là 23:59:59 của ngày hiệu lực - 1
      effectiveMoment = moment(dateEffect).startOf("date");
      expiryMoment = moment(effectiveMoment).add(1, "years").subtract(1, "day").endOf("date");
    } else {
      // Ngày hiệu lực là ngày hiện tại: giờ bắt đầu đúng lúc tạo hợp đồng
      effectiveMoment = moment(dateEffect || new Date())
        .hour(now.hour())
        .minute(now.minute())
        .second(now.second());
      expiryMoment = moment(effectiveMoment).add(1, "years");
    }

    const effectiveDateIso = effectiveMoment.format("YYYY-MM-DDTHH:mm:ss+07:00");
    const expiryDateIso = expiryMoment.format("YYYY-MM-DDTHH:mm:ss+07:00");

    const payload = {
      packageCode: selectedPackage?.code,
      ...form,
      buyerProvince: finalBuyerProvince,
      buyerWard: finalBuyerWard,
      address: `${finalBuyerAddress}, ${finalBuyerWard?.name || ''}, ${finalBuyerProvince?.name || ''}`,
      insuredAddress: `${form.insuredAddress}, ${form.insuredWard?.name || ''}, ${form.insuredProvince?.name || ''}`,
      effectiveDate: effectiveDateIso,
      dateEffect: effectiveDateIso,
      expiryDate: expiryDateIso,
      expireDate: expiryDateIso,
    };

    console.log("payload", payload);

    dispatch(
      createOrderCallback(TYPE_INSURANCE.FIRE_EXPLOSION, selectedPackage, payload, undefined, undefined, (resp) => {
        const { id } = resp;
        console.log("id", id);
        dispatch(payOrder(id, PRODUCT_TYPE.fire_explosion));
      })
    );
  };

  return (
    <div className="fire-explosion">
      <div className="fire-explosion__body">
        <BuyerInfoSection
          form={form}
          updateField={updateField}
          setFieldTouched={setFieldTouched}
          insuredAddressError={insuredAddressError}
          insuredProvinceError={insuredProvinceError}
          insuredWardError={insuredWardError}
          buyerProvinceError={buyerProvinceError}
          buyerWardError={buyerWardError}
          buyerNameError={buyerNameError}
          phoneError={phoneError}
          emailError={emailError}
          addressError={addressError}
          provinces={provinces}
          insuredWards={insuredWards}
          buyerWards={buyerWards}
          loadingProvinces={loadingProvinces}
          loadingInsuredWards={loadingInsuredWards}
          loadingBuyerWards={loadingBuyerWards}
          handleInsuredProvinceChange={handleInsuredProvinceChange}
          handleBuyerProvinceChange={handleBuyerProvinceChange}
          sameAsInsuredAddress={sameAsInsuredAddress}
          handleToggleSameAddress={handleToggleSameAddress}
        />

        <h2
          ref={packagesSectionRef}
          className="fire-explosion__section-title fire-explosion__section-title--spaced"
        >
          Gói bảo hiểm
        </h2>
        <FireExplosionPackagesPanel
          packages={displayPackages}
          selectedId={selectedId}
          onSelectPackage={setSelectedId}
          companyVariants={selectedTier?.variants}
          selectedVariantKey={selectedVariantKey}
          onSelectVariant={setSelectedVariantKey}
          dateEffect={dateEffect}
          onDateChange={setDateEffect}
          minDateEffect={minDateEffect}
          maxDateEffect={maxDateEffect}
          onOpenBenefitSheet={openBenefitSheet}
        />
      </div>

      <BenefitSheetModal
        isOpen={benefitSheetOpen}
        onClose={closeBenefitSheet}
        activeTab={benefitSheetTab}
        onTabChange={setBenefitSheetTab}
      />

      <footer className="fire-explosion__footer">
        {selectedTier && selectedPackage && (
          <div className="fire-explosion__summary-bar">
            <div className="fire-explosion__summary-info">
              <span className="fire-explosion__summary-label">Gói đã chọn</span>
              <span className="fire-explosion__summary-value">
                {selectedTier.label} · {formatVnd(selectedPackage.premium)}
              </span>
            </div>
            <button
              type="button"
              className="fire-explosion__summary-detail-btn"
              onClick={scrollToPackages}
            >
              Xem chi tiết &rsaquo;
            </button>
          </div>
        )}
        <button
          type="button"
          disabled={creatingOrder || createPayOrder}
          className="fire-explosion__buy-btn"
          onClick={handleBuy}
        >
          {creatingOrder || createPayOrder ? "Đang xử lý..." : "Mua ngay"}
        </button>
      </footer>
    </div>
  );
};

export default FireExplosionInsuranceScreen;
